import clsx from "clsx";

import { Typo } from "../Typo";

export type StatusPillStatus =
  | "Active"
  | "Pending KYC"
  | "Rejected"
  | "Archived"
  | "Inactive"
  | "New"
  | "Neutral";

type StatusPillProps = {
  status: StatusPillStatus;
  label?: string;
  className?: string;
};

// Mirrors the canvas's `.chip` colour modes (`styles.css`): mode-amber for
// Pending KYC, mode-neutral-bold for Archived/Inactive, mode-blue for New.
// These override the handoff spec's §3 values (`surface-tags-blue` /
// `text-informative-bold` / `text-secondary`), which are paler than what the
// canvas renders — canvas markup wins per the design source hierarchy in
// CLAUDE.md, and SubIBReport's own local StatusPill/NewTag already build to it.
const VARIANTS: Record<
  Exclude<StatusPillStatus, "Active">,
  { bg: string; text: string }
> = {
  "Pending KYC": {
    bg: "bg-surface-accent-amber-subtler",
    text: "text-text-accent-amber-boldest",
  },
  Rejected: { bg: "bg-surface-tonal-default", text: "text-text-primary" },
  Archived: { bg: "bg-surface-tags-grey", text: "text-text-primary" },
  Inactive: { bg: "bg-surface-tags-grey", text: "text-text-primary" },
  New: {
    bg: "bg-surface-accent-blue-subtler",
    text: "text-text-accent-blue-boldest",
  },
  // Generic non-client-status badge (e.g. CampaignPicker's "Default" tag) —
  // reuses the same neutral tokens as Archived/Inactive.
  Neutral: { bg: "bg-surface-tags-grey", text: "text-text-primary" },
};

// `.chip` + `.chip--2xs`: 20px tall, fully rounded, `border: 0`, 4px padding on
// the chip plus 2px on the label — 6px effective. Not `rounded-md px-2 py-0.5`.
const CHIP_CLASSES =
  "inline-flex h-5 shrink-0 items-center whitespace-nowrap rounded-full px-1";

/** Active renders no pill — only abnormal/notable states get one. */
export const StatusPill = ({ status, label, className }: StatusPillProps) => {
  if (status === "Active") return null;
  const { bg, text } = VARIANTS[status];

  return (
    <span className={clsx(CHIP_CLASSES, bg, className)}>
      <Typo
        element="span"
        type="label2XS"
        textColor={text}
        className="whitespace-nowrap px-0.5"
      >
        {label ?? status}
      </Typo>
    </span>
  );
};

type StatusPillOverflowChipProps = {
  count: number;
  label?: string;
  className?: string;
};

/**
 * "+N" overflow chip — the canvas's `.chip.mode-tonal`: same shape as
 * StatusPill, `--surface-tonal-default` fill, secondary text.
 * (`surface-tags-subtle-dark-base` is the same `rgba(3,20,46,0.05)` value under
 * a different token name; kept because the handoff spec §3 names it.)
 */
export const StatusPillOverflowChip = ({
  count,
  label,
  className,
}: StatusPillOverflowChipProps) => (
  <span
    className={clsx(
      CHIP_CLASSES,
      "bg-surface-tags-subtle-dark-base",
      className,
    )}
  >
    <Typo
      element="span"
      type="label2XS"
      textColor="text-text-secondary"
      className="whitespace-nowrap px-0.5"
    >
      {label ?? `+${count}`}
    </Typo>
  </span>
);
